import { IGetLandingPage, IGlobalConfigData, IUGCLabel } from '@interfaces';
import { IModulesData } from '../../interfaces/IPages';
import { ICruiseSearchItem } from '@interfaces/cruise';
import React from 'react';
import { createSearchUrl } from '../components/molecules/cruise-search-mask/utils';
import { OfferTeaser } from '../components/sections/offer-teaser/offer-teaser';
import { normalizeLabels } from '../utils/normalizeLabels';
import { getRatingLabel } from '../utils/ugc-options';
import { generateCruiseUrl } from '../utils/url-generation';
import { LocaleConfigContext } from '../contexts/locale-config/locale-config';

interface IOfferTeaserProps {
    getLandingPage: IGetLandingPage;
    modulesData: IModulesData;
    globalConfigData: IGlobalConfigData;
}

// getOfferItems function adding priorityCruise (Dreamlines packages) as a first item if it exists
const getOfferItems = (items: ICruiseSearchItem[], priorityCruise: ICruiseSearchItem[]) => {
    if (!priorityCruise?.length) return items || [];

    const firstPriorityCruiseItem = priorityCruise[0];
    const currentOffersWithoutPriorityCruise = items.filter(
        (item) => item.cruiseId !== firstPriorityCruiseItem.cruiseId,
    );

    const mergedItems = [firstPriorityCruiseItem, ...currentOffersWithoutPriorityCruise];

    // remove the last item if we got more than six offers per page
    if (mergedItems.length > 6) {
        mergedItems.pop();
    }
    return mergedItems;
};

export const OfferTeaserMod = ({
    getLandingPage,
    modulesData,
    globalConfigData,
}: IOfferTeaserProps) => {
    const localeConfig = React.useContext(LocaleConfigContext);
    const baseUrl = `https://www.${localeConfig.url}/${localeConfig.slugs.search}`;

    const filterType = encodeURIComponent('filterParams[ship][0]');
    const selection = { key: String(getLandingPage.id), title: '' };
    const sortType = 'sortString=' + localeConfig.defaultSortingString;
    let searchUrl = `${baseUrl}?${sortType}`;

    if (getLandingPage.type === 'ship') {
        searchUrl = `${baseUrl}?${filterType}=${getLandingPage.id}&${sortType}`;
    } else if (getLandingPage.type === 'cruiseline') {
        searchUrl = `${createSearchUrl(baseUrl, { brand: selection })}&${sortType}`;
    } else if (getLandingPage.type === 'destination') {
        searchUrl = `${createSearchUrl(baseUrl, { region: selection })}&${sortType}`;
    }

    if (!modulesData || !modulesData.cruiseSearchlandingpageOffer) return null;

    const { totalCount, items, priorityCruise } = modulesData.cruiseSearchlandingpageOffer;
    if (items.length < 1) {
        console.warn('cruise search in frontpage returned no offers for slug', getLandingPage.slug);
        return null;
    }

    const offerItems = getOfferItems(items, priorityCruise).map((item) => {
        const shipImage = item.ship.images;
        const routeImages = item.images.filter((image) => image.tags.includes('route'));
        const images = item.images.filter((image) => !image.tags.includes('route'));
        const teaserImages = [...routeImages, ...shipImage, ...images];
        const days = item.days;
        const sails = item?.sailings?.sails;
        const defaultCabinAvailabilityMaxThreshold = 50;
        const cabinAvailabilityMaxThreshold = globalConfigData
            ? globalConfigData.cabinAvailabilityMaxThreshold
            : defaultCabinAvailabilityMaxThreshold;
        const price = item?.sailings?.minPrice || 0;
        const highDemandObj =
            item.ugcLabels &&
            item.ugcLabels.find((ugcLabel: IUGCLabel) => {
                return ugcLabel.label === 'highDemand';
            });

        return {
            nid: item.cruiseId,
            startHarbour: item?.startHarbour?.city,
            shipName: item?.ship?.title,
            icons: item.icons,
            numOfNights: days > 1 ? days - 1 : '',
            numOfPorts: item?.routeMeta?.uniqueStops,
            numOfDaysAtSea: item?.routeMeta?.daysAtSea,
            images: teaserImages,
            companyLogo: item?.brand?.logo,
            companyName: item?.brand?.title,
            ratingValue: item.ratingStars ? item.ratingStars.toFixed(1) : undefined,
            ratingString: getRatingLabel(item.ratingStars),
            ratingCount: item.ratingCount,
            minPrice: price / 2,
            discount: item?.sailings?.maxDiscount,
            visits: item?.stats?.visits,
            cabinStockThreshold: cabinAvailabilityMaxThreshold,
            highDemand: Boolean(highDemandObj),
            cheapestCabinType: item?.topSail?.rate?.cabinType,
            highlights: item.topHarboursHighlights || [],
            withFlight: item.includesFlight,
            departureDate: item?.sailings?.earliestDepartureDate,
            returnDate: item?.sailings?.latestReturnDate,
            totalStock: item?.sailings?.totalStock,
            onBoardCredit: item?.sailings?.onBoardCredit,
            numOfSails: sails ? sails.length : 0,
            textLabelItems: normalizeLabels({
                ugcLabels: item.ugcLabels || [],
                excludeCluster: ['productData'],
            }),
            redirectUrl: generateCruiseUrl(
                localeConfig.slugs.cruise.prefix,
                item?.title,
                Number(item?.cruiseId),
            ),
            requestType: getLandingPage.type,
        };
    });

    return (
        <OfferTeaser
            key="offerTeaser"
            totalOfferCount={totalCount}
            headline={getLandingPage.title}
            // @TODO: there's a misalignement of props here
            items={offerItems as any}
            searchUrl={searchUrl}
            requestType={getLandingPage.type}
        />
    );
};
